import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent, type RefObject } from 'react'
import { nanoid } from 'nanoid'
import pLimit from 'p-limit'
import { useStore } from 'react-redux'
import { isFileAcceptedForUserFilesUpload } from '@/user_files/utils/file-type-utils'
import { RuntimeFeatureFlag } from '@/feature-flags/definitions'
import { useRuntimeFeatureFlag } from '@/feature-flags/useRuntimeFeatureFlag'
import { validateUserFilesUploadFile } from '@/user_files/utils/upload-validation'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { selectActiveMatterId } from '@/matter/store/selectors'
import { UppyFileUploadActions } from '@/uppy-file-upload/store/slice'
import { useUppyUpload } from '@/uppy-file-upload/hooks/useUppyUpload'
import { selectFileListRequest, selectFilesForFolder } from '@/user_files/files/store/selectors'
import { selectFolderNameById } from '@/user_files/folders/store/selectors'
import { getFolderFiles } from '@/user_files/files/fetch/get-folder-files'
import { categorizeUploadFiles, type UploadCandidate } from '@/user_files/drive/utils/categorize-upload-files'
import { groupDroppedFilesByTarget } from '@/user_files/drive/utils/group-dropped-files-by-target'
import { runSerializedForFolder } from '@/user_files/drive/utils/run-serialized-for-folder'
import { addQueuedUploadFilenames } from '@/user_files/drive/utils/add-queued-upload-filenames'
import { getInvalidFolderSegmentError } from '@/user_files/drive/utils/get-invalid-folder-segment-error'
import { getOrCreateFolderPathId } from '@/user_files/folders/utils/get-or-create-folder-id'
import { kMaxConcurrentNestedFolderResolutions, kNoUploadableFilesMessage } from '@/user_files/drive/constants'
import { GlobalToastType, kToastDurationMediumMs } from '@/constants/constants-ui'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { RootState } from '@/store/store'
import type { Conflict } from '@/user_files/drive/components/ConflictModal'
import { UserFilesActions } from '@/user_files/files/store/slice'

type PendingConflict = Conflict & { clientTmpId: string; file: File; folderId: string }

type UseDriveUploadParams = {
  folderId: string | null
  isFileAccepted?: (file: File) => boolean
  validateUploadFile?: (file: File) => Promise<string | null>
  onFileInitialized?: (params: { clientTmpId: string; serverFileId: string; storagePath: string; folderId: string }) => void
  onFileRegistered?: (params: { clientTmpId: string; folderId: string; filename: string; sourceFile: File }) => void
  onUploadCompleted?: (params: { clientTmpId: string; serverFileId?: string; folderId: string }) => void
  onUploadFailed?: (params: { clientTmpId: string; serverFileId?: string; folderId: string; error: string }) => void
}

type UseDriveUploadReturn = {
  /** Files awaiting a folder listing or an explicit filename-conflict decision. */
  isPreparingFiles: boolean
  /** Opens the file picker. */
  openUploadPicker: () => void
  /** Opens the directory picker, which uploads a whole folder tree. */
  openFolderPicker: () => void
  /** Whether the upload is disabled. */
  disabled: boolean
  /** Ref to the hidden file input.
   * This allows us to support uploading files from outside of the Dropzone. */
  fileInputRef: RefObject<HTMLInputElement>
  /** Ref to the hidden directory-picker input, alongside `fileInputRef`. */
  folderInputRef: RefObject<HTMLInputElement>
  /** Adds files to the upload queue. */
  addFiles: (files: File[]) => void
  /** When the user picks file(s) or a folder via the OS dialog. */
  onFilesPicked: (e: ChangeEvent<HTMLInputElement>) => void
  /** Cancels an upload. */
  cancelUpload: (clientTmpId: string) => void
  /** Removes a file from Redux (queued row or finished processing row). */
  removeFile: (clientTmpId: string) => void
  /** Whether there are queued files. */
  hasQueuedFiles: boolean
  /** State and handlers for the filename conflict resolution modal. */
  conflictDialog: {
    /** Whether the conflict modal is open. */
    open: boolean
    /** One entry per file whose name collides with an existing file in the folder. */
    conflicts: Conflict[]
    /** Names already taken in this folder — used for per-field validation in the modal. */
    occupiedNames: ReadonlySet<string>
    /** Per-folder occupied names, keyed by target folder id — scopes validation correctly when a
     * drop's conflicts span more than one folder (current folder + nested subfolders). */
    occupiedNamesByFolderId: ReadonlyMap<string, ReadonlySet<string>>
    /** Proceed with the (possibly user-edited) resolved names for conflicting files. */
    onConfirm: (resolvedNames: string[]) => void
    /** Discard all files that had name conflicts. */
    onCancel: () => void
  }
}

function getAddFileErrorMessage(error: unknown): string {
  return error instanceof Error && error.message ? error.message : 'Unable to upload file.'
}

function getWaitingFileKey(file: File): string {
  return `${file.name}:${file.size}:${file.lastModified}:${file.type}`
}

/**
 * Drive upload mechanics: Uppy + hidden file input + auto-start.
 *
 * Files arrive via drag/drop (`addFiles`) or OS picker (`onFilesPicked`)
 */
export function useDriveUpload({
  folderId,
  isFileAccepted,
  validateUploadFile = validateUserFilesUploadFile,
  onFileInitialized,
  onFileRegistered,
  onUploadCompleted,
  onUploadFailed,
}: UseDriveUploadParams): UseDriveUploadReturn {
  const { enabled: emailFileProcessingEnabled } = useRuntimeFeatureFlag(RuntimeFeatureFlag.EMAIL_FILE_PROCESSING)
  const acceptsFile = useCallback(
    (file: File) => (isFileAccepted ? isFileAccepted(file) : isFileAcceptedForUserFilesUpload(file, emailFileProcessingEnabled)),
    [isFileAccepted, emailFileProcessingEnabled]
  )
  const fileInputRef = useRef<HTMLInputElement>(null)
  const folderInputRef = useRef<HTMLInputElement>(null)
  const ownedClientTmpIdsRef = useRef<Set<string>>(new Set())
  const lastProcessedWaitingFilesRef = useRef<File[] | null>(null)
  const dispatch = useAppDispatch()
  const store = useStore<RootState>()
  const fileListRequest = useAppSelector((state) => selectFileListRequest(state, { folderId }))
  // Recorded on every row this hook creates so the upload stays attributed to the matter it was
  // queued in, even after the user switches matters mid-upload.
  const activeMatterId = useAppSelector(selectActiveMatterId)

  const [pendingConflicts, setPendingConflicts] = useState<PendingConflict[]>([])
  const [conflictDialogOpen, setConflictDialogOpen] = useState(false)
  const [occupiedNamesSnapshot, setOccupiedNamesSnapshot] = useState<ReadonlySet<string>>(new Set())
  const [occupiedNamesByFolderId, setOccupiedNamesByFolderId] = useState<ReadonlyMap<string, ReadonlySet<string>>>(new Map())
  const [filesWaitingForFolderFiles, setFilesWaitingForFolderFiles] = useState<File[]>([])

  const { uppy, queuedFiles, allFiles, startUpload, cancelUpload, removeFile } = useUppyUpload({
    onFileInitialized,
    onUploadCompleted,
    onUploadFailed,
  })

  const ownedQueuedFiles = useMemo(() => queuedFiles.filter((file) => ownedClientTmpIdsRef.current.has(file.client.clientTmpId)), [queuedFiles])
  const ownedFilesAreUploading = useMemo(
    () =>
      allFiles.some(
        (file) =>
          ownedClientTmpIdsRef.current.has(file.client.clientTmpId) && (file.client.uploadStatus === 'initializing' || file.client.uploadStatus === 'uploading')
      ),
    [allFiles]
  )
  const disabled = !folderId || ownedFilesAreUploading

  const registerOwnedUpload = useCallback(
    ({ clientTmpId, filename, targetFolderId, sourceFile }: { clientTmpId: string; filename: string; targetFolderId: string; sourceFile: File }) => {
      const isNewUploadForOwner = !ownedClientTmpIdsRef.current.has(clientTmpId)
      ownedClientTmpIdsRef.current.add(clientTmpId)
      if (isNewUploadForOwner) {
        onFileRegistered?.({ clientTmpId, folderId: targetFolderId, filename, sourceFile })
      }
    },
    [onFileRegistered]
  )

  /** Registers a file as an inline "validation failed" row, generating a clientTmpId if the caller doesn't already have one. */
  const failFileInline = useCallback(
    (params: { file: File; filename: string; targetFolderId: string; error: string; clientTmpId?: string }) => {
      const clientTmpId = params.clientTmpId ?? nanoid()
      registerOwnedUpload({ clientTmpId, filename: params.filename, targetFolderId: params.targetFolderId, sourceFile: params.file })
      dispatch(
        UppyFileUploadActions.validationFailedFileAdded({
          clientTmpId,
          filename: params.filename,
          fileSize: params.file.size,
          folderId: params.targetFolderId,
          matterId: activeMatterId,
          error: params.error,
        })
      )
    },
    [dispatch, registerOwnedUpload, activeMatterId]
  )

  const addToUppy = useCallback(
    (file: File, name: string, clientTmpId: string, targetFolderId: string) => {
      try {
        uppy.addFile({ name, type: file.type, data: file, source: 'Local', meta: { clientTmpId, folderId: targetFolderId, matterId: activeMatterId } })
      } catch (error) {
        console.error('Error adding file:', error)
        failFileInline({ file, filename: name, targetFolderId, error: getAddFileErrorMessage(error), clientTmpId })
      }
    },
    [uppy, failFileInline, activeMatterId]
  )

  const validateAndAddToUppy = useCallback(
    async (candidates: UploadCandidate[]) => {
      const validated: UploadCandidate[] = []

      for (const { file, name, clientTmpId, folderId: targetFolderId } of candidates) {
        let validationError: string | null = null
        try {
          validationError = await validateUploadFile(file)
        } catch (error) {
          console.error('Error validating file:', error)
          validationError = 'Unable to validate file.'
        }

        if (validationError) {
          dispatch(
            UppyFileUploadActions.validationFailedFileAdded({
              clientTmpId,
              filename: name,
              fileSize: file.size,
              folderId: targetFolderId,
              matterId: activeMatterId,
              error: validationError,
            })
          )
          continue
        }

        validated.push({ file, name, clientTmpId, folderId: targetFolderId })
      }

      for (const { file, name, clientTmpId, folderId: targetFolderId } of validated) {
        addToUppy(file, name, clientTmpId, targetFolderId)
      }
    },
    [dispatch, addToUppy, validateUploadFile, activeMatterId]
  )

  /**
   * Accept/conflict-detection/queueing pass, shared by the current folder and every resolved
   * nested subfolder. `pathLabel` names the destination for the conflict modal, since one drop's
   * conflicts can span several folders.
   */
  const queueFilesForFolder = useCallback(
    (targetFolderId: string, files: File[], occupiedNamesSeed: ReadonlySet<string>, pathLabel: string | undefined) => {
      const { unsupported, conflicts, candidates, baseOccupiedNames } = categorizeUploadFiles({
        files,
        folderId: targetFolderId,
        occupiedNamesSeed,
        isFileAccepted: acceptsFile,
      })

      for (const { clientTmpId, file } of unsupported) {
        registerOwnedUpload({ clientTmpId, filename: file.name, targetFolderId, sourceFile: file })
        dispatch(
          UppyFileUploadActions.unsupportedTypeFileAdded({
            clientTmpId,
            filename: file.name,
            fileSize: file.size,
            folderId: targetFolderId,
            matterId: activeMatterId,
            mimeType: file.type,
          })
        )
      }

      for (const { clientTmpId, file, name } of candidates) {
        registerOwnedUpload({ clientTmpId, filename: name, targetFolderId, sourceFile: file })
        dispatch(
          UppyFileUploadActions.fileValidationStarted({
            clientTmpId,
            filename: name,
            fileSize: file.size,
            folderId: targetFolderId,
            matterId: activeMatterId,
          })
        )
      }

      if (conflicts.length > 0) {
        setPendingConflicts((current) => [...current, ...conflicts.map((conflict) => ({ ...conflict, folderPathLabel: pathLabel }))])
        // A single drop can touch several target folders (current folder + nested subfolders).
        // occupiedNamesSnapshot merges them as a conservative fallback; occupiedNamesByFolderId
        // scopes validation to this specific folder so a name free here isn't wrongly rejected
        // just because it's taken in a different folder from the same batch.
        setOccupiedNamesSnapshot((current) => new Set([...current, ...baseOccupiedNames]))
        setOccupiedNamesByFolderId((current) => {
          const next = new Map(current)
          next.set(targetFolderId, new Set([...(next.get(targetFolderId) ?? []), ...baseOccupiedNames]))
          return next
        })
        setConflictDialogOpen(true)
      }

      void validateAndAddToUppy(candidates)
    },
    [dispatch, acceptsFile, registerOwnedUpload, validateAndAddToUppy, activeMatterId]
  )

  /** Processes files dropped directly into the currently open folder (no containing local folder). */
  const processCurrentFolderFiles = useCallback(
    (files: File[]) => {
      if (!folderId) return
      if (files.length === 0) return

      // lazy load the folder files for conflict detection if the list is not already loaded; do not add files to uppy until the list is loaded
      if (fileListRequest.status !== 'success') {
        lastProcessedWaitingFilesRef.current = null
        setFilesWaitingForFolderFiles((currentFiles) => [...currentFiles, ...files])
        if (fileListRequest.status !== 'loading') {
          dispatch(UserFilesActions.fetchFilesForFolder({ folderId }))
        }
        return
      }

      // Build the set of names already taken in this folder (existing + queued uploads).
      const state = store.getState()
      const existingFiles = selectFilesForFolder(state, { folderId })
      const occupiedNames = new Set<string>(existingFiles.map((f) => f.original_filename))
      addQueuedUploadFilenames(occupiedNames, state.uppyFileUploadState, folderId)

      // Label these rows too, so a mixed drop doesn't leave the current-folder rows blank next to
      // labelled nested-folder ones.
      queueFilesForFolder(folderId, files, occupiedNames, selectFolderNameById(state, { folderId }) ?? undefined)
    },
    [dispatch, fileListRequest.status, folderId, store, queueFilesForFolder]
  )

  // Falls back to the currently open folder as the bucket the user will see the error in, since
  // there's no real target folder to attribute this to when creating it is what failed; includes
  // the attempted path in the message since the folder name alone won't be visible.
  const failNestedFolderGroup = useCallback(
    (files: File[], pathLabel: string, error: string) => {
      if (!folderId) return
      for (const file of files) {
        failFileInline({ file, filename: file.name, targetFolderId: folderId, error: `Couldn't create folder "${pathLabel}": ${error}` })
      }
    },
    [folderId, failFileInline]
  )

  /**
   * Processes files dropped as part of a local folder: resolves (finds or creates) the matching
   * nested subfolder under the currently open folder, then runs the same accept/conflict pass.
   * Newly created subfolders can't have pre-existing files, but the target may also resolve to an
   * already-existing subfolder, so occupied names are always fetched fresh from the server rather
   * than relying on this folder being the one currently loaded/reactive in Redux.
   */
  const processNestedFolderGroup = useCallback(
    async (segments: string[], files: File[]) => {
      if (!folderId) return

      const pathLabel = segments.join('/')

      const invalidSegmentError = getInvalidFolderSegmentError(segments)
      if (invalidSegmentError) {
        failNestedFolderGroup(files, pathLabel, invalidSegmentError)
        return
      }

      let targetFolderId: string
      try {
        targetFolderId = await getOrCreateFolderPathId({ dispatch, getState: store.getState, rootId: folderId, segments })
      } catch (error) {
        console.error('Error creating nested upload folder:', error)
        failNestedFolderGroup(files, pathLabel, getAddFileErrorMessage(error))
        return
      }

      await runSerializedForFolder(targetFolderId, async () => {
        const occupiedNames = new Set<string>()
        try {
          const response = await getFolderFiles(targetFolderId)
          response.files.forEach((f) => occupiedNames.add(f.original_filename))
        } catch (error) {
          console.error('Error fetching nested upload folder files:', error)
          // Conflict detection can't be trusted without the current file list — surface an
          // inline error instead of silently uploading without name-collision protection,
          // consistent with the current-folder file-list-load failure below.
          for (const file of files) {
            failFileInline({ file, filename: file.name, targetFolderId, error: 'Could not check for existing files in this folder. Try again.' })
          }
          return
        }

        const state = store.getState()
        addQueuedUploadFilenames(occupiedNames, state.uppyFileUploadState, targetFolderId)

        queueFilesForFolder(targetFolderId, files, occupiedNames, pathLabel)
      })
    },
    [dispatch, folderId, store, failFileInline, queueFilesForFolder, failNestedFolderGroup]
  )

  const addFilesToUppy = useCallback(
    (files: File[]) => {
      if (!folderId) return
      if (files.length === 0) return

      const { currentFolderFiles, nestedGroups } = groupDroppedFilesByTarget(files)

      // Everything the drop carried was OS metadata, so nothing gets queued and there is no
      // upload row to attach an inline message to — a toast is the only way to explain why the
      // drop appears to have done nothing.
      if (currentFolderFiles.length === 0 && nestedGroups.length === 0) {
        dispatch(openGlobalToast({ type: GlobalToastType.WARNING, message: kNoUploadableFilesMessage, durationMs: kToastDurationMediumMs }))
        return
      }

      if (currentFolderFiles.length > 0) {
        processCurrentFolderFiles(currentFolderFiles)
      }

      // Bounded so a drop with many distinct top-level folders doesn't fire an unbounded
      // burst of concurrent folder-creation requests; shared path prefixes are already
      // deduped independently inside getOrCreateFolderPathId.
      const limit = pLimit(kMaxConcurrentNestedFolderResolutions)
      for (const group of nestedGroups) {
        void limit(() => processNestedFolderGroup(group.segments, group.files))
      }
    },
    [dispatch, folderId, processCurrentFolderFiles, processNestedFolderGroup]
  )

  const getUniqueWaitingFiles = useCallback((files: File[]) => {
    if (lastProcessedWaitingFilesRef.current === files) return []
    lastProcessedWaitingFilesRef.current = files

    const waitingFileKeys = new Set<string>()
    const uniqueFiles: File[] = []
    for (const file of files) {
      const fileKey = getWaitingFileKey(file)
      if (waitingFileKeys.has(fileKey)) continue
      waitingFileKeys.add(fileKey)
      uniqueFiles.push(file)
    }

    return uniqueFiles
  }, [])

  useEffect(() => {
    setFilesWaitingForFolderFiles([])
    lastProcessedWaitingFilesRef.current = null
  }, [folderId])

  useEffect(() => {
    if (fileListRequest.status === 'error') {
      if (filesWaitingForFolderFiles.length === 0) return
      const waitingFilesBatch = filesWaitingForFolderFiles
      const files = getUniqueWaitingFiles(waitingFilesBatch)
      if (files.length === 0) return

      if (folderId) {
        for (const file of files) {
          failFileInline({ file, filename: file.name, targetFolderId: folderId, error: 'Matter Files could not be loaded. Try again.' })
        }
      }
      setFilesWaitingForFolderFiles((currentFiles) => (currentFiles === waitingFilesBatch ? [] : currentFiles))
    }
  }, [fileListRequest.status, filesWaitingForFolderFiles, folderId, failFileInline, getUniqueWaitingFiles])

  // when the folder files are loaded and ready for name conflict detection, add the waiting files to the uppy queue
  useEffect(() => {
    if (fileListRequest.status !== 'success') return
    if (filesWaitingForFolderFiles.length === 0) return

    const waitingFilesBatch = filesWaitingForFolderFiles
    const files = getUniqueWaitingFiles(waitingFilesBatch)
    if (files.length === 0) return

    setFilesWaitingForFolderFiles((currentFiles) => (currentFiles === waitingFilesBatch ? [] : currentFiles))
    processCurrentFolderFiles(files)
  }, [processCurrentFolderFiles, fileListRequest.status, filesWaitingForFolderFiles, getUniqueWaitingFiles])

  const confirmConflictUpload = useCallback(
    (resolvedNames: string[]) => {
      const candidates: UploadCandidate[] = pendingConflicts.map((conflict, i) => ({
        file: conflict.file,
        name: resolvedNames[i] ?? conflict.resolvedName,
        clientTmpId: conflict.clientTmpId,
        folderId: conflict.folderId,
      }))

      setPendingConflicts([])
      setOccupiedNamesSnapshot(new Set())
      setOccupiedNamesByFolderId(new Map())
      setConflictDialogOpen(false)

      for (const { clientTmpId, name, file, folderId: targetFolderId } of candidates) {
        registerOwnedUpload({ clientTmpId, filename: name, targetFolderId, sourceFile: file })
        dispatch(
          UppyFileUploadActions.fileValidationStarted({
            clientTmpId,
            filename: name,
            fileSize: file.size,
            folderId: targetFolderId,
            matterId: activeMatterId,
          })
        )
      }

      void validateAndAddToUppy(candidates)
    },
    [pendingConflicts, dispatch, validateAndAddToUppy, registerOwnedUpload, activeMatterId]
  )

  const cancelConflictUpload = useCallback(() => {
    setPendingConflicts([])
    setOccupiedNamesSnapshot(new Set())
    setOccupiedNamesByFolderId(new Map())
    setConflictDialogOpen(false)
  }, [])

  const handleFileSelect = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files ? Array.from(e.target.files) : []
      // Reset first so the same folder can be picked twice in a row, and so the input is always
      // empty before the next pick — a cancelled OS dialog then can't fire `change` at all, which
      // is what keeps the empty-pick warning below from firing on a cancel.
      e.target.value = ''

      // An empty folder pick yields zero files, so nothing downstream ever sees it and there is no
      // upload row to carry an inline message. The drop path fires this from DriveDropzone; the
      // picker had no equivalent, and choosing an empty folder is an easy mistake to make.
      if (files.length === 0) {
        dispatch(openGlobalToast({ type: GlobalToastType.WARNING, message: kNoUploadableFilesMessage, durationMs: kToastDurationMediumMs }))
        return
      }

      addFilesToUppy(files)
    },
    [addFilesToUppy, dispatch]
  )

  const openUploadPicker = useCallback(() => {
    if (disabled) return
    fileInputRef.current?.click()
  }, [disabled])

  const openFolderPicker = useCallback(() => {
    if (disabled) return
    folderInputRef.current?.click()
  }, [disabled])

  const hasQueuedFiles = ownedQueuedFiles.length > 0

  useEffect(() => {
    if (hasQueuedFiles && !ownedFilesAreUploading) {
      startUpload(ownedQueuedFiles)
    }
  }, [hasQueuedFiles, ownedFilesAreUploading, ownedQueuedFiles, startUpload])

  const cancelOwnedUpload = useCallback(
    (clientTmpId: string) => {
      ownedClientTmpIdsRef.current.delete(clientTmpId)
      cancelUpload(clientTmpId)
    },
    [cancelUpload]
  )

  const removeOwnedFile = useCallback(
    (clientTmpId: string) => {
      ownedClientTmpIdsRef.current.delete(clientTmpId)
      removeFile(clientTmpId)
    },
    [removeFile]
  )

  const conflictDialogConflicts = useMemo(
    () =>
      pendingConflicts.map(({ originalName, resolvedName, folderId: targetFolderId, folderPathLabel }) => ({
        originalName,
        resolvedName,
        folderId: targetFolderId,
        folderPathLabel,
      })),
    [pendingConflicts]
  )

  return {
    isPreparingFiles: filesWaitingForFolderFiles.length > 0 || pendingConflicts.length > 0,
    openUploadPicker,
    openFolderPicker,
    disabled,
    fileInputRef,
    folderInputRef,
    addFiles: addFilesToUppy,
    onFilesPicked: handleFileSelect,
    cancelUpload: cancelOwnedUpload,
    removeFile: removeOwnedFile,
    hasQueuedFiles,
    conflictDialog: {
      open: conflictDialogOpen,
      conflicts: conflictDialogConflicts,
      occupiedNames: occupiedNamesSnapshot,
      occupiedNamesByFolderId,
      onConfirm: confirmConflictUpload,
      onCancel: cancelConflictUpload,
    },
  }
}
